import { useEffect, useMemo, useState } from "react";

import { Alert, Card, CircularProgress, Grid } from "@mui/material";
import dayjs from "dayjs";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiInput from "components/VuiInput";
import VuiAutocomplete from "components/AutoComplete";
import HotelSelector from "components/HotelSelector";

import { FaPlus, FaClipboardList, FaPen, FaTrashRestore } from "react-icons/fa";

import CreateTemplateDialog from "./components/CreateTemplateDialog";
import OperationsWorkspace from "./components/OperationsWorkspace";
import OperationsSearch from "./components/OperationsSearch";

import { DUMMY_WORKSPACE, DUMMY_OPERATIONS_TEMPLATES } from "variables";
import useCommon from "hooks/useCommon";
import { getOperationsTemplates } from "store/slice/operationsSlice";
import { createOperationsTemplate } from "store/slice/operationsSlice";
import { resetCreateOperationsTemplateState } from "store/slice/operationsSlice";
import { getOperationsTemplateById } from "store/slice/operationsSlice";
import { updateOperationsTemplate } from "store/slice/operationsSlice";
import { getOperationsEntry } from "store/slice/operationsSlice";
import { clearOperationsEntry } from "store/slice/operationsSlice";
import Snackbar from "components/Snackbar";
import { saveOperationsEntry } from "store/slice/operationsSlice";
import { deleteOperationsTemplate } from "store/slice/operationsSlice";
import DeleteTemplateDialog from "./components/DeleteTemplateDialog";
import VuiTooltip from "components/VuiTooltip";

import usePermissions from "hooks/usePermissions";
import colors from "assets/theme/base/colors";
import useHotelDateTime from "hooks/useHotelDateTime";
import { isEntryEditable } from "./entryValues";

const DailyOperationsHub = () => {
  const { DISPATCH, STORE, NAVIGATE } = useCommon();
  const { can } = usePermissions();
  const { getHotelNow, timezone } = useHotelDateTime();
  const today = getHotelNow().format("YYYY-MM-DD");

  const { primary, dark, light } = colors;

  const hotelIds = STORE((state) => state.ui.hotelIds);

  const {
    templates,
    operationsEntry,
    entryLoading,
    entryError,
    fetchLoading,
    fetchError,
    createLoading,
    createError,
    updateLoading,
    fetchOneLoading,
    updateSuccess,
    updateError,
    selectedTemplate: templateDetails,
    deleteLoading,
    deleteSuccess,
  } = STORE((state) => state.operations);

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [createTemplateOpen, setCreateTemplateOpen] = useState(false);
  const [editTemplateOpen, setEditTemplateOpen] = useState(false);
  const [deleteTemplateOpen, setDeleteTemplateOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(today);
  const [snackbar, setSnackbar] = useState({
    show: false,
    color: "success",
    title: "",
    content: "",
    icon: "check",
  });

  const showSnackbar = (data) =>
    setSnackbar({
      show: true,
      ...data,
    });

  const closeSnackbar = () =>
    setSnackbar((prev) => ({
      ...prev,
      show: false,
    }));

  // const templates = DUMMY_OPERATIONS_TEMPLATES;

  const hasTemplates = templates.length > 0 || Boolean(selectedTemplate);

  const activeTemplate = selectedTemplate;

  const handleOpenCreateTemplate = () => {
    DISPATCH(resetCreateOperationsTemplateState());
    setCreateTemplateOpen(true);
  };

  const handleEditTemplate = () => {
    if (!activeTemplate) return;

    DISPATCH(getOperationsTemplateById(activeTemplate.value));

    setEditTemplateOpen(true);
  };

  const buildTemplateSectionsPayload = (sections) => {
    return sections.map((section, index) => {
      const sectionPayload = {
        ...(section.sectionId ? { sectionId: section.sectionId } : {}),
        title: section.title,
        subheading: section.subheading,
        fieldType: section.fieldType,
        sortOrder: index + 1,
        isActive: true,
      };

      if (["checkbox_list", "bullet_point_list", "radio_buttons"].includes(section.fieldType)) {
        sectionPayload.fieldConfig = {
          placeholder: "",
          options: section.options || [],
        };
      }

      return sectionPayload;
    });
  };

  const handleCreateTemplateSave = async (payload) => {
    const requestBody = {
      hotelId: hotelIds?.[0],
      name: payload.templateName,
      workspaceHeading: payload.heading,
      description: payload.description,
      sections: buildTemplateSectionsPayload(payload.sections),
    };

    try {
      const res = await DISPATCH(createOperationsTemplate(requestBody)).unwrap();

      if (res?.success) {
        showSnackbar({
          color: "success",
          title: "Template Created",
          content: res?.message || "Operations template created successfully",
          icon: "check",
        });
        DISPATCH(getOperationsTemplates(hotelIds[0]));
        setCreateTemplateOpen(false);
      }
    } catch (err) {
      // showSnackbar({
      //   color: "error",
      //   title: "Error",
      //   content: err?.message || err || "Failed to create template",
      //   icon: "warning",
      // });
    }
  };

  const handleUpdateTemplateSave = async (payload) => {
    try {
      const sectionsPayload = buildTemplateSectionsPayload(payload.sections);

      const res = await DISPATCH(
        updateOperationsTemplate({
          templateId: activeTemplate.value,
          data: {
            lastKnownUpdatedAt: templateDetails.updatedAt,
            name: payload.templateName,
            workspaceHeading: payload.heading,
            description: payload.description,
            sections: sectionsPayload,
          },
        })
      ).unwrap();

      if (res?.success) {
        showSnackbar({
          color: "success",
          title: "Template Updated",
          content: res?.message || "Operations template updated successfully",
          icon: "check",
        });

        setEditTemplateOpen(false);
        DISPATCH(getOperationsTemplates(hotelIds[0]));

        DISPATCH(
          getOperationsEntry({
            hotelId: hotelIds[0],
            templateId: activeTemplate.value,
            operationDate: selectedDate,
          })
        );
      }
    } catch (err) {
      // showSnackbar({
      //   color: "error",
      //   title: "Error",
      //   content: err?.message || err || "Failed to update template",
      //   icon: "warning",
      // });
    }
  };

  const handleSaveWorkspace = async (payload) => {
    if (!can("editOperations") || !isEntryEditable(operationsEntry) || entryLoading) return;
    try {
      const res = await DISPATCH(saveOperationsEntry(payload)).unwrap();

      // console.log("saveOperationsEntry res", res);

      showSnackbar({
        color: "success",
        title: "Workspace Saved",
        content: "Operations workspace saved successfully",
        icon: "check",
      });
    } catch (err) {
      showSnackbar({
        color: "error",
        title: "Error",
        content: err?.message || err || "Failed to save workspace",
        icon: "warning",
      });
    }
  };

  const handleDeleteTemplate = async () => {
    try {
      const res = await DISPATCH(
        deleteOperationsTemplate({
          templateId: activeTemplate.value,
          lastKnownUpdatedAt: activeTemplate.updatedAt,
        })
      ).unwrap();

      showSnackbar({
        color: "success",
        title: "Template Deleted",
        content: res?.message || "Template deleted successfully",
        icon: "check",
      });

      setDeleteTemplateOpen(false);
    } catch (err) {
      showSnackbar({
        color: "error",
        title: "Error",
        content: err?.message || err || "Failed to delete template",
        icon: "warning",
      });
    }
  };

  useEffect(() => {
    if (hotelIds.length) {
      DISPATCH(getOperationsTemplates(hotelIds[0]));
    }
  }, [hotelIds, deleteSuccess, updateSuccess]);

  useEffect(() => {
    if (!templates.length) {
      setSelectedTemplate(null);
      return;
    }

    const updatedSelected = templates.find(
      (template) => template.value === selectedTemplate?.value
    );

    if (updatedSelected) {
      setSelectedTemplate(updatedSelected);
    } else {
      setSelectedTemplate(templates[0]);
    }
  }, [templates]);

  useEffect(() => {
    if (hotelIds?.[0] && activeTemplate?.value && selectedDate) {
      DISPATCH(
        getOperationsEntry({
          hotelId: hotelIds[0],
          templateId: activeTemplate.value,
          operationDate: selectedDate,
        })
      );
    } else {
      DISPATCH(clearOperationsEntry());
    }
  }, [hotelIds, activeTemplate?.value, selectedDate, today, DISPATCH]);

  useEffect(() => {
    setSelectedDate(getHotelNow().format("YYYY-MM-DD"));
    setSearchInput("");
  }, [hotelIds?.[0], timezone]);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <VuiBox pb={1} pt={0}>
        <Card>
          <VuiBox p={0}>
            <VuiBox mb={1.5}>
              <Grid
                container
                spacing={1.5}
                alignItems="center"
                justifyContent={{
                  xs: "center",
                  md: "space-between",
                }}
              >
                <Grid item xs={12}>
                  <VuiBox display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                    <VuiTypography variant="lg">Daily Operations</VuiTypography>
                    <VuiBox display="flex" alignItems="center" gap={1} sx={{ flexShrink: 0 }}>
                      {can("deleteOperations") && (
                        <VuiTooltip title="Deleted Templates">
                          <VuiButton
                            variant="text"
                            color="primary"
                            size="small"
                            aria-label="Deleted Templates"
                            sx={{ minWidth: 32, width: 32, height: 32, p: 0 }}
                            onClick={() => NAVIGATE("/daily-operations/deleted-templates")}
                          >
                            <FaTrashRestore size={12} />
                          </VuiButton>
                        </VuiTooltip>
                      )}
                      <VuiBox sx={{ width: 140, flexShrink: 0 }}>
                        <VuiInput
                          gradient
                          fullWidth
                          size="small"
                          type="date"
                          inputProps={{ "aria-label": "Operation date" }}
                          value={selectedDate}
                          disabled={!hasTemplates}
                          onChange={(e) => {
                            if (e.target.value)
                              setSelectedDate(e.target.value);
                          }}
                        />
                      </VuiBox>
                    </VuiBox>
                  </VuiBox>
                </Grid>

                <Grid item xs={12}>
                  <VuiBox
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={2}
                    flexWrap="wrap"
                  >
                    <VuiBox>
                      <HotelSelector minWidth={200} />
                    </VuiBox>

                    {can("viewOperations") && (
                      <VuiBox sx={{ width: { xs: "100%", sm: 200 }, minWidth: 0 }}>
                        <OperationsSearch
                          key={hotelIds?.[0] || "no-hotel"}
                          hotelId={hotelIds?.[0]}
                          input={searchInput}
                          onInputChange={(value) => {
                            setSearchInput(value);
                            if (!value.trim()) {
                              setSelectedDate(getHotelNow().format("YYYY-MM-DD"));
                              setSelectedTemplate(templates[0] || null);
                            }
                          }}
                          onSelect={(suggestion) => {
                            setSelectedTemplate(
                              templates.find((item) => item.value === suggestion.templateId) || {
                                value: suggestion.templateId,
                                label: suggestion.templateName,
                              }
                            );
                            setSelectedDate(suggestion.operationDateKey);
                          }}
                        />
                      </VuiBox>
                    )}

                    {/* TEMPLATE */}
                    <VuiBox
                      sx={{
                        width: {
                          xs: "100%",
                          sm: 200,
                          md: 220,
                        },
                      }}
                    >
                      <VuiAutocomplete
                        options={templates}
                        value={selectedTemplate}
                        disabled={!hasTemplates}
                        placeholder="Select template"
                        fullWidth
                        getOptionLabel={(option) => option?.label || ""}
                        isOptionEqualToValue={(option, value) => option?.value === value?.value}
                        onChange={(_, selected) => {
                          setSelectedTemplate(selected || null);
                        }}
                      />
                    </VuiBox>

                    {/* BUTTONS */}
                    <VuiBox
                      display="flex"
                      justifyContent={{
                        xs: "center",
                        md: "flex-end",
                      }}
                      gap={1}
                      sx={{
                        width: {
                          xs: "100%",
                          sm: "auto",
                        },
                      }}
                    >
                      {/* {activeTemplate && (
                        <VuiButton  variant="outlined" onClick={handleEditTemplate}>
                          <FaPen size={11} style={{ marginRight: 6 }} />
                          Edit
                        </VuiButton>
                      )} */}
                      {can("createOperations") && (
                        <VuiButton
                          color="primary"
                          variant="contained"
                          onClick={handleOpenCreateTemplate}
                        >
                          <FaPlus size={12} style={{ marginRight: 6 }} />
                          New Template
                        </VuiButton>
                      )}
                    </VuiBox>
                  </VuiBox>
                </Grid>
              </Grid>
            </VuiBox>

            {/* LOADING */}
            {fetchLoading && (
              <VuiBox py={10} display="flex" justifyContent="center" alignItems="center">
                <CircularProgress color="primary" />
              </VuiBox>
            )}

            {/* ERROR */}
            {!fetchLoading && fetchError && (
              <VuiBox py={10} textAlign="center">
                <Alert severity="error">
                  {fetchError?.message || "Failed to load operations templates"}
                </Alert>
              </VuiBox>
            )}

            {/* NO TEMPLATES */}
            {!fetchLoading && !fetchError && !hasTemplates && (
              <VuiBox
                py={10}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
              >
                <VuiBox
                  width="90px"
                  height="90px"
                  borderRadius="xl"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  mb={3}
                  sx={{
                    background: light.secondary,
                    border: `1px solid ${dark.main}`,
                  }}
                >
                  <FaClipboardList size={36} sx={{ color: primary.main }} />
                </VuiBox>

                <VuiTypography variant="h4" fontWeight="bold" textAlign="center" mb={1}>
                  No Operations Templates Yet
                </VuiTypography>

                <VuiTypography variant="caption" textAlign="center" maxWidth="700px" mb={4}>
                  Create your first Operations Template to start managing daily hotel workflows
                  through a centralized collaborative workspace.
                </VuiTypography>

                {can("createOperations") && (
                  <VuiButton color="primary" size="large" onClick={handleOpenCreateTemplate}>
                    <FaPlus size={12} style={{ marginRight: 6 }} />
                    Create First Template
                  </VuiButton>
                )}
              </VuiBox>
            )}

            {/* TEMPLATE NOT SELECTED */}
            {!fetchLoading && !fetchError && hasTemplates && !activeTemplate && (
              <VuiBox
                py={10}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
              >
                <VuiBox
                  width="80px"
                  height="80px"
                  borderRadius="xl"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  mb={3}
                  sx={{
                    background: light.secondary,
                    border: `1px solid ${dark.main}`,
                  }}
                >
                  <FaClipboardList size={32} sx={{ color: primary.main }} />
                </VuiBox>

                <VuiTypography variant="h4" fontWeight="bold" textAlign="center" mb={1}>
                  Select an Operations Template
                </VuiTypography>

                <VuiTypography variant="caption" textAlign="center" maxWidth="650px">
                  Choose a template from the selector above to load the daily workspace for the
                  selected date.
                </VuiTypography>
              </VuiBox>
            )}

            {/* WORKSPACE */}
            {!fetchLoading && !fetchError && hasTemplates && activeTemplate && (
              <OperationsWorkspace
                template={activeTemplate}
                searchQuery={searchInput}
                onSave={handleSaveWorkspace}
                onDelete={() => setDeleteTemplateOpen(true)}
                onEdit={handleEditTemplate}
                selectedDate={selectedDate}
              />
            )}
          </VuiBox>
        </Card>
      </VuiBox>

      <CreateTemplateDialog
        open={createTemplateOpen}
        loading={createLoading}
        error={createError?.message || createError || ""}
        onClose={() => setCreateTemplateOpen(false)}
        onSave={handleCreateTemplateSave}
      />

      <CreateTemplateDialog
        open={editTemplateOpen}
        template={templateDetails}
        loading={updateLoading}
        fetchLoading={fetchOneLoading}
        error={updateError?.message || updateError || ""}
        onClose={() => {
          if (fetchOneLoading || updateLoading) return;

          setEditTemplateOpen(false);
        }}
        onSave={handleUpdateTemplateSave}
      />

      <DeleteTemplateDialog
        open={deleteTemplateOpen}
        template={activeTemplate}
        loading={deleteLoading}
        onClose={() => setDeleteTemplateOpen(false)}
        onConfirm={handleDeleteTemplate}
      />

      <Snackbar
        color={snackbar.color}
        icon={snackbar.icon}
        title={snackbar.title}
        content={snackbar.content}
        open={snackbar.show}
        onClose={closeSnackbar}
        close={closeSnackbar}
      />
    </DashboardLayout>
  );
};

export default DailyOperationsHub;
